'use client';

import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { ChevronRight, FileText, Loader2 } from 'lucide-react';
import { MarketingLayout } from '@/components/layout/MarketingLayout';
import { legalApi } from '@/lib/api';
import { legalPath } from '@/lib/legal-url';
import fallbackCatalogJson from '@/lib/legal-fallback-catalog.json';
import type { LegalDocumentPayload } from '@/lib/legal-server';

type LegalListItem = {
  title: string;
  slug: string;
  category?: string;
  public_path?: string;
  version: string;
  effective_date: string | null;
};

const fallbackDocs: LegalListItem[] = Object.values(
  fallbackCatalogJson as Record<string, LegalDocumentPayload>
).map((d) => ({
  title: d.title,
  slug: d.slug,
  category: d.category,
  public_path: d.public_path,
  version: d.version,
  effective_date: d.effective_date,
}));

export default function LegalIndexClient() {
  const searchParams = useSearchParams();
  const category = searchParams?.get('category')?.trim() || '';
  const [docs, setDocs] = useState<LegalListItem[]>(() => {
    return category
      ? fallbackDocs.filter((d) => d.category?.toLowerCase() === category.toLowerCase())
      : fallbackDocs;
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    legalApi
      .list(category ? { category } : undefined)
      .then((res) => {
        const items = res.data?.data;
        if (!cancelled) {
          if (items && items.length > 0) {
            setDocs(items);
          } else {
            const filtered = category
              ? fallbackDocs.filter((d) => d.category?.toLowerCase() === category.toLowerCase())
              : fallbackDocs;
            setDocs(filtered);
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          const filtered = category
            ? fallbackDocs.filter((d) => d.category?.toLowerCase() === category.toLowerCase())
            : fallbackDocs;
          setDocs(filtered);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [category]);

  const heading = useMemo(
    () => (category ? category : 'Legal Center'),
    [category],
  );

  return (
    <MarketingLayout contentClassName="mt-2 md:mt-3">
      <div className="mx-auto w-full max-w-[1100px] px-4 py-8 md:py-12">
        <nav className="mb-5 text-[13px] text-[var(--color-text-muted)]" aria-label="Breadcrumb">
          <Link href="/" className="hover:text-[var(--color-text)]">
            Home
          </Link>
          <span className="mx-1.5 text-[var(--color-border)]">/</span>
          <span className="text-[var(--color-text)]">Legal</span>
        </nav>

        <header className="mb-6 border-b border-[var(--color-border)] pb-5">
          <h1 className="text-[1.625rem] font-semibold tracking-tight text-[var(--color-text)] sm:text-[1.85rem]">
            {heading}
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[var(--color-text-muted)]">
            {category
              ? `Published ${category.toLowerCase()} documents.`
              : 'Official EnthiosHub legal documents, policies, and agreements.'}
          </p>
          {category ? (
            <p className="mt-3 text-sm">
              <Link
                href="/legal"
                className="font-medium text-[var(--color-primary-hover)] hover:underline"
              >
                View all documents
              </Link>
            </p>
          ) : null}
        </header>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-7 w-7 animate-spin text-[var(--color-text-muted)] opacity-50" />
          </div>
        ) : docs.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)]">No published documents found.</p>
        ) : (
          <ul className="divide-y divide-[var(--color-border)] border border-[var(--color-border)] bg-[var(--color-surface)]">
            {docs.map((d) => (
              <li key={d.slug}>
                <Link
                  href={legalPath(d)}
                  className="flex items-center justify-between gap-4 px-4 py-3.5 transition-colors hover:bg-[var(--color-surface-muted)]"
                >
                  <span className="flex min-w-0 items-start gap-3">
                    <FileText className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-text-muted)]" />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-[var(--color-text)]">
                        {d.title}
                      </span>
                      {d.category ? (
                        <span className="mt-0.5 block text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
                          {d.category}
                        </span>
                      ) : null}
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2 text-[12px] text-[var(--color-text-muted)]">
                    <span>v{d.version}</span>
                    <ChevronRight className="h-4 w-4" aria-hidden />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </MarketingLayout>
  );
}
